import React, { useState, useEffect, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import Button from '../../componentsV2/Button/Button';
import { Plus, TagNameIcon } from '../../componentsV2/Icons/Icons';
import EditableTable from '../../componentsV2/table/editableTable';
import { getTags, deleteTag, updateTag } from '../../utils/api/tag';
import { formatNumericDate } from '../../utils/dates';
import { DialogsProvider, ToastProvider } from '../../providers';
import { withContextProvider } from '../../hoc';
import EmptyState from '../../componentsV2/EmptyState/EmptyState';
import NoSearchResults from '../../componentsV2/NoSearchResults/NoSearchResults';
import Pagination from '../../componentsV2/pagination/Pagination';
import Search from '../../componentsV2/SearchBar/Search';
import TagModal from '../../componentsV2/Tags/TagsTabbedModal';
import Breadcrumbs from '../../componentsV2/Breadcrumbs/Breadcrumbs';
import PagesHeader from '../../componentsV2/Headers/PagesHeader';
import { updateSearchParamsAndUrl } from '../../utils/updateSearchParamsAndUrl';
import useSyncedSearchParams from '../../hooks/useSyncedSearchParams';
import { parseParams } from '../../utils/parseParams';
import { getRoleChecks } from '../../utils/roles';
import { useUrlModals } from '../../utils/useUrlModals';
import ResultCount from '../../componentsV2/ResultCount/ResultCount';
import TableSkeletonLoader from '../../componentsV2/LoadingSkeletons/TableSkeletonLoader';

const parseTagParams = (searchParams) => parseParams(searchParams, {
  name: {},
  sortBy: {},
  direction: {},
  perPage: { type: 'int', default: 20 },
  page: { type: 'int', default: 1 },
});

const getTagPage = (searchParams) => parseInt(searchParams.get('page'), 10) || 1;

const TagsPage = ({ organizationId, currentUserRole }) => {
  const [tags, setTags] = useState([]);
  const [totalTags, setTotalTags] = useState(0);
  const [totalUnfilteredTags, setTotalUnfilteredTags] = useState(0);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const breadcrumbLinks = [{ name: 'Tags' }];
  const defaultPerPage = 20;
  const { isManager } = getRoleChecks(currentUserRole);

  const {
    params, setParams, currentPage, setCurrentPage,
  } = useSyncedSearchParams(parseTagParams, getTagPage);

  const perPage = params.perPage || defaultPerPage;
  const currentOffset = (currentPage - 1) * perPage;

  const { modals, openModal, closeModal } = useUrlModals({ addTag: {} });
  const tagModal = modals.addTag || { isOpen: false, value: null };

  const fetchTags = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getTags(organizationId, params);
      const data = await response.json();
      if (data.error) {
        setError(data.error);
      } else {
        setTags(data.tags);
        const isSearchActive = !!params.name;
        setTotalTags(isSearchActive ? data.result_count : data.total_count);
        setTotalUnfilteredTags(data.total_count);
      }
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, params]);

  const formattedTagsData = tags.map(tag => ({
    id: tag.id,
    name: tag.name,
    date: formatNumericDate(tag.updated_at),
    sites: tag.sites_count,
  }));

  const onPageChange = (page) => {
    const updatedParams = { ...params, page, perPage };
    setCurrentPage(page);
    setParams(updatedParams);
    updateSearchParamsAndUrl({ params: updatedParams });
  };

  const onSortChange = (sortBy, direction) => {
    const updatedParams = {
      ...params, sortBy, direction, page: 1,
    };
    setCurrentPage(1);
    setParams(updatedParams);
    updateSearchParamsAndUrl({ params: updatedParams });
  };

  const onUpdateTag = async (updatedTag) => {
    const tagId = updatedTag.id;
    const updatedTagData = {
      tag: {
        name: updatedTag.name,
      },
    };

    try {
      const response = await updateTag(tagId, organizationId, updatedTagData);

      if (response.status === 422) {
        const errorData = await response.json();
        const nameErrors = errorData?.errors || [];
        const isDuplicate = Array.isArray(nameErrors)
        && nameErrors.some((msg) => /unique|taken/i.test(msg));
        return { success: false, error: isDuplicate ? 'duplicate' : 'validation' };
      }
      if (!response.ok) {
        return { success: false, error: 'server' };
      }
      return { success: true };
    } catch (err) {
      return { success: false, error: 'network' };
    }
  };

  const onDeleteTag = async (tagId) => {
    await deleteTag(organizationId, tagId);
    window.location.reload();
  };

  const onCreate = async () => {
    closeModal('addTag');
    window.location.reload();
  };

  const handleTagRowClick = (id, sitesTotal) => {
    if (sitesTotal > 0) window.location.href = `/tags/${id}`;
  };

  const mappedActiveSearchParams = {
    name: params.name || '',
  };

  const columns = [
    {
      key: 'name', header: 'Name', isEditable: true, width: '35%', fontWeight: 'normal', shouldDisplayIcon: true, icon: TagNameIcon, isSortable: true,
    },
    {
      key: 'date', header: 'Date', isEditable: false, width: '25%', isSortable: true,
    },
    {
      key: 'sites', header: 'Sites', isEditable: false, width: '20%', isSortable: true, headerClassName: 'justify-center', className: 'justify-center',
    },
  ];

  const searchFields = [{ label: 'Name', name: 'name', type: 'text' }];
  const chipsSettings = { name: { prefix: 'tag', type: 'text' } };

  const handleSearch = (values) => {
    const updatedParams = {
      ...params, name: values.name || '', page: 1, perPage,
    };
    setParams(updatedParams);
    setCurrentPage(1);
    updateSearchParamsAndUrl({ params: updatedParams });
  };

  useEffect(() => { fetchTags(); }, [organizationId, params, fetchTags]);

  let contentToRender;

  if (isLoading) {
    contentToRender = (
      <div className="flex justify-center w-full">
        <div className="pt-8 w-full">
          <TableSkeletonLoader
            numRows={perPage}
            numColumns={4}
            skeletonHeight={16}
            skeletonWidth="70%"
          />
        </div>
      </div>
    );
  } else if (tags.length > 0) {
    contentToRender = (
      <>
        <EditableTable
          dataSource={formattedTagsData}
          columns={columns}
          isDeleteEnabled={isManager}
          onUpdate={onUpdateTag}
          onDelete={onDeleteTag}
          itemType="Tag"
          onSortChange={onSortChange}
          onRowClick={handleTagRowClick}
          defaultSortOrder={{ sortBy: params.sortBy || 'name', order: params.direction || 'asc' }}
        />
        <Pagination
          itemsPerPage={perPage}
          totalItems={totalTags}
          currentPage={currentPage}
          setCurrentPage={onPageChange}
        />
      </>
    );
  } else if (totalUnfilteredTags === 0) {
    contentToRender = <EmptyState pageType="tags" onAdd={() => openModal('addTag', true)} />;
  } else {
    contentToRender = <NoSearchResults pageType="tags" />;
  }

  return (
    <DialogsProvider>
      <div className="ml-2.75">
        <header>
          <div className="flex justify-between items-start w-full">
            {/* Breadcrumbs */}
            <Breadcrumbs links={breadcrumbLinks} />
            <div className="self-start">
              <Search onSearch={handleSearch} fields={searchFields} title="search tags" chipsSettings={chipsSettings} activeSearchParams={mappedActiveSearchParams} />
            </div>
          </div>
        </header>
        <div className="flex w-full justify-between items-center pb-4">
          <PagesHeader name="tags" label="Tags" size="h-9" />

          {!isLoading && tags.length > 0 && (
            <div className="flex-1 flex justify-center">
              <ResultCount
                offset={currentOffset}
                pageSize={tags.length}
                total={totalTags}
                unfilteredTotal={totalUnfilteredTags}
                viewType="table"
                pageType="tags"
              />
            </div>
          )}

          {!isLoading && tags.length > 0 && (
            <Button
              label="New Tag"
              Icon={Plus}
              aria-label="New Tag"
              onClick={() => openModal('addTag', true)}
            />
          )}
          {tagModal.isOpen && (
            <TagModal
              handleClose={() => closeModal('addTag')}
              orgId={organizationId}
              onCreate={onCreate}
              title="Tags"
              isTagAsTabShowing={false}
              isCreateNewTabShowing
              params={params}
            />
          )}
        </div>
        {error && <p>Error fetching tags: {error.message}</p>}
        {contentToRender}
      </div>
    </DialogsProvider>
  );
};

TagsPage.propTypes = {
  organizationId: PropTypes.number.isRequired,
  currentUserRole: PropTypes.string.isRequired,
};

export default withContextProvider(ToastProvider)(TagsPage);
